import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { Send, UserMinus, MoreHorizontal, UserCheck, Share2, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * HostTools
 * ---------
 * Lightweight in-page host controls. Tools appear only when needed:
 *   - ParticipantsAdmin: tap-to-action list (Remove before event / Did not show after)
 *   - ShareToggle: only on private activities
 *   - HostNote: send a single free-form message to participants
 *
 * Design rules (per directive):
 *   - actions appear only when needed
 *   - no persistent controls, no admin language
 *   - "I can shape this if I need to", not "I'm managing people"
 */
export function HostTools({ activity, hasEnded, onChanged }) {
  if (!activity?.is_host) return null;
  return (
    <div className="space-y-4" data-testid="host-tools">
      <ParticipantsAdmin activityId={activity.activity_id} hasEnded={hasEnded} onChanged={onChanged} />
      <ShareToggle activity={activity} onChanged={onChanged} />
      <HostNote activityId={activity.activity_id} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Participants admin — tap-to-action row menu
// ---------------------------------------------------------------------------

function ParticipantsAdmin({ activityId, hasEnded, onChanged }) {
  const [rows, setRows] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/activities/${activityId}/participants`, {
        headers: authHeaders(), withCredentials: true,
      });
      setRows(res.data?.participant_details || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activityId]);

  const doDidNotShow = async (row, next) => {
    setBusy(row.user_id);
    try {
      await axios.post(
        `${API}/api/activities/${activityId}/attendance`,
        { user_id: row.user_id, did_not_show: next },
        { headers: authHeaders(), withCredentials: true },
      );
      setRows(prev => prev.map(p => p.user_id === row.user_id ? { ...p, did_not_show: next } : p));
      setOpenRow(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    } finally { setBusy(null); }
  };

  const doRemove = async (row) => {
    setBusy(row.user_id);
    try {
      await axios.post(
        `${API}/api/activities/${activityId}/participants/${row.user_id}/remove`,
        {}, { headers: authHeaders(), withCredentials: true },
      );
      setRows(prev => prev.filter(p => p.user_id !== row.user_id));
      setOpenRow(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove');
    } finally { setBusy(null); }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <Card
      className="p-4"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="host-participants"
    >
      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
        Participants
      </h3>
      <ul className="divide-y" style={{ borderColor: 'var(--fy-border-subtle)' }}>
        {rows.map(r => {
          const muted = r.did_not_show;
          const initials = (r.user_name || 'U').slice(0, 1).toUpperCase();
          return (
            <li key={r.user_id} className="py-2" data-testid={`host-participant-${r.user_id}`}>
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                  style={{
                    backgroundColor: muted ? 'var(--fy-surface-muted)' : 'var(--lk-sage-muted, #EDF1E8)',
                    color: muted ? 'var(--fy-text-disabled)' : 'var(--lk-sage)',
                  }}
                >
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" style={{ color: muted ? 'var(--fy-text-disabled)' : 'var(--fy-text-primary)' }}>
                    {r.user_name || 'Participant'}
                  </p>
                  {muted && (
                    <p className="text-[11px]" style={{ color: 'var(--fy-text-disabled)' }}>Marked as did not show</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpenRow(openRow === r.user_id ? null : r.user_id)}
                  className="p-2 rounded-full hover:opacity-70 transition-opacity"
                  aria-label="Open participant actions"
                  data-testid={`host-participant-menu-${r.user_id}`}
                >
                  <MoreHorizontal className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
                </button>
              </div>
              {openRow === r.user_id && (
                <div className="mt-2 ml-11 flex flex-wrap gap-2" data-testid={`host-participant-actions-${r.user_id}`}>
                  {hasEnded ? (
                    <button
                      type="button"
                      disabled={busy === r.user_id}
                      onClick={() => doDidNotShow(r, !r.did_not_show)}
                      className="text-xs px-2.5 py-1 rounded-full transition-opacity hover:opacity-80"
                      style={{ color: 'var(--fy-text-secondary)', border: '1px solid var(--fy-border-subtle)' }}
                      data-testid={`host-participant-didnotshow-${r.user_id}`}
                    >
                      {r.did_not_show ? (
                        <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> Undo</span>
                      ) : (
                        <span className="flex items-center gap-1"><UserMinus className="w-3 h-3" /> Did not show</span>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busy === r.user_id}
                      onClick={() => doRemove(r)}
                      className="text-xs px-2.5 py-1 rounded-full transition-opacity hover:opacity-80"
                      style={{ color: 'var(--fy-text-secondary)', border: '1px solid var(--fy-border-subtle)' }}
                      data-testid={`host-participant-remove-${r.user_id}`}
                    >
                      <span className="flex items-center gap-1"><UserMinus className="w-3 h-3" /> Remove</span>
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Share toggle — only on private activities
// ---------------------------------------------------------------------------

function ShareToggle({ activity, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(!!activity.sharing_enabled);

  // Public activities are always shareable; no toggle needed.
  if ((activity.visibility || 'public') === 'public') return null;

  const toggle = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/activities/${activity.activity_id}`,
        { sharing_enabled: !enabled },
        { headers: authHeaders(), withCredentials: true },
      );
      setEnabled(!enabled);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    } finally { setSaving(false); }
  };

  return (
    <Card
      className="p-4"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="host-share-toggle"
    >
      <div className="flex items-start gap-3">
        <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-secondary)' }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
            Private activity
          </p>
          <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
            {enabled
              ? 'Participants can share this with their circles.'
              : 'Only people you invite can see this.'}
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          aria-pressed={enabled}
          className="flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
          style={{ backgroundColor: enabled ? 'var(--lk-sage)' : 'var(--fy-border-subtle)' }}
          data-testid="host-share-toggle-btn"
        >
          <span
            className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
            style={{ transform: enabled ? 'translateX(22px)' : 'translateX(2px)' }}
          />
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Host note — single free-form message to participants (broadcast "note")
// ---------------------------------------------------------------------------

function HostNote({ activityId }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const ref = useRef(null);

  const send = async () => {
    const body = msg.trim();
    if (!body) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/api/activities/${activityId}/broadcast`,
        { type: 'note', message: body },
        { headers: authHeaders(), withCredentials: true },
      );
      toast.success('Note sent to participants');
      setMsg('');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => ref.current?.focus(), 50); }}
        className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm hover:opacity-90 transition-opacity"
        style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
        data-testid="host-note-open"
      >
        <Send className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
        Send a note to participants
      </button>
    );
  }

  return (
    <Card
      className="p-4"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="host-note-panel"
    >
      <Textarea
        ref={ref}
        value={msg}
        onChange={(e) => setMsg(e.target.value.slice(0, 200))}
        placeholder="One note. Keep it short — directions, parking, a heads-up."
        rows={3}
        className="mb-2 resize-none"
        data-testid="host-note-textarea"
      />
      <div className="flex items-center justify-between">
        <p className="text-[11px]" style={{ color: 'var(--fy-text-disabled)' }}>{msg.length}/200</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setOpen(false); setMsg(''); }}
            className="text-xs px-3 py-1.5 rounded-full"
            style={{ color: 'var(--fy-text-secondary)' }}
            data-testid="host-note-cancel"
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={send}
            disabled={sending || !msg.trim()}
            className="text-xs px-3 py-1.5 h-auto rounded-full"
            style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
            data-testid="host-note-send"
          >
            {sending ? 'Sending…' : 'Send note'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
